/**
 * Idle-lock state machine (SECURITY_AUDIT.md §3.4).
 *
 * When the app goes to background/inactive, we record the timestamp. On
 * return to foreground, if the backgrounded interval exceeded the user's
 * configured threshold, the app transitions to LOCKED — a full-screen
 * overlay renders on top of every screen until the user re-authenticates.
 *
 * This module holds only the state + subscribe/notify. AppState wiring,
 * threshold fetching, and the UI live at the app root.
 *
 * Cold launch is not a concern here: the app already routes fresh starts
 * through Splash/Login/Biometric, so the idle-lock is only relevant for
 * background/foreground transitions within an authenticated session.
 */

export type IdleLockListener = (locked: boolean) => void;

let backgroundedAt: number | null = null;
let locked = false;
const listeners = new Set<IdleLockListener>();

function emit(): void {
  listeners.forEach((fn) => {
    try {
      fn(locked);
    } catch {
      /* swallow — a broken listener must not block others */
    }
  });
}

/**
 * Subscribe to lock-state changes. Returns an unsubscribe function.
 * The listener is NOT fired with the current value on subscribe — read
 * it once via `isLocked()` if needed.
 */
export function subscribeToLock(fn: IdleLockListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function isLocked(): boolean {
  return locked;
}

/**
 * Record the moment the app went to background. Idempotent within a
 * single background session — only the first call is kept, so
 * background→inactive→background sequences don't reset the clock.
 */
export function markBackgrounded(now: number = Date.now()): void {
  if (backgroundedAt === null) {
    backgroundedAt = now;
  }
}

/**
 * Called on AppState 'active'. If the app was backgrounded for at least
 * `thresholdMs`, transitions to locked. Always consumes the background
 * timestamp so the next background→active cycle starts fresh.
 *
 * Returns the (possibly new) locked state after this call.
 */
export function maybeLockOnForeground(
  thresholdMs: number,
  now: number = Date.now(),
): boolean {
  if (backgroundedAt === null) return locked;
  const elapsed = now - backgroundedAt;
  backgroundedAt = null;
  if (elapsed >= thresholdMs && !locked) {
    locked = true;
    emit();
  }
  return locked;
}

/**
 * Force-lock the app immediately. Used by callers that want to lock
 * without waiting for a background/foreground cycle (e.g., a "Lock now"
 * button, or by code paths that detect suspicious activity).
 */
export function lockNow(): void {
  if (!locked) {
    locked = true;
    emit();
  }
}

/**
 * Clear the locked flag after a successful re-auth. Also clears any
 * dangling background timestamp so the next cycle starts clean.
 */
export function unlock(): void {
  backgroundedAt = null;
  if (locked) {
    locked = false;
    emit();
  }
}

/**
 * Test-only: reset all module state. Never call from production code.
 */
export function __resetIdleLockForTests(): void {
  backgroundedAt = null;
  locked = false;
  listeners.clear();
}
